// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useState, useEffect } from 'react';

import { scrollableModalBodyStyle } from '../../../styles/constants';
import type { ThresholdSettingsModalProps, Threshold } from '../../../types';
import { settingsAPI } from '../../../utils/apiClient';
import { Button } from '../../common/Button';

interface ThresholdGroup {
  title: string;
  icon: string;
  description: string;
  items: Threshold[];
}

interface ThresholdGroups {
  battery: ThresholdGroup;
  signal: ThresholdGroup;
  noSignal: ThresholdGroup;
  temperature: ThresholdGroup;
  humidity: ThresholdGroup;
}

// Maps each group to its *_enabled setting key
const GROUP_ENABLED_KEY: Record<string, string> = {
  battery: 'battery_low_enabled',
  signal: 'signal_weak_enabled',
  noSignal: 'no_signal_enabled',
  temperature: 'temperature_enabled',
  humidity: 'humidity_enabled',
};

/**
 * ThresholdSettingsModal Component
 *
 * Modal for configuring maintenance warning thresholds for lf_device.
 * - Displays all configurable threshold values grouped by warning type
 * - Each group has an enable/disable toggle
 * - Allows SuperAdmin to adjust values
 * - Saves changes to database
 * - Option to trigger immediate health check after changes
 */
export const ThresholdSettingsModal: React.FC<ThresholdSettingsModalProps> = ({
  onClose,
  onSave,
  inline,
}) => {
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  const [editedValues, setEditedValues] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [triggerHealthCheck, setTriggerHealthCheck] = useState<boolean>(false);

  useEffect(() => {
    fetchThresholds();
  }, []);

  const fetchThresholds = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const data = await settingsAPI.getThresholds();
      if (data.success) {
        // Keep only lf_device thresholds (exclude all prefixed infra keys)
        const deviceThresholds: Threshold[] = (data.thresholds || []).filter(
          (t: Threshold) =>
            !t.setting_key.startsWith('lf_beacon_') &&
            !t.setting_key.startsWith('lf_checkin_') &&
            !t.setting_key.startsWith('lf_gateway_')
        );
        setThresholds(deviceThresholds);

        const initial: Record<string, number> = {};
        deviceThresholds.forEach((t) => {
          initial[t.setting_key] = t.setting_value;
        });
        setEditedValues(initial);
      } else {
        setError(data.error || 'Failed to fetch thresholds');
      }
    } catch (err) {
      console.error('Error fetching thresholds:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key: string, value: string): void => {
    setEditedValues((prev) => ({
      ...prev,
      [key]: parseFloat(value),
    }));
  };

  const hasChanges = (): boolean => {
    return thresholds.some((t) => editedValues[t.setting_key] !== t.setting_value);
  };

  const handleSave = async (): Promise<void> => {
    if (!hasChanges()) {
      alert('No changes to save');
      return;
    }

    if (
      !window.confirm(
        'Save threshold changes?\n\n' +
          'Warning: This will affect how warnings are detected for all devices.\n' +
          (triggerHealthCheck
            ? 'A health check will be triggered immediately to re-evaluate all devices.\n'
            : '') +
          '\nAre you sure?'
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const changedKeys = thresholds
        .filter((t) => editedValues[t.setting_key] !== t.setting_value)
        .map((t) => t.setting_key);

      for (const key of changedKeys) {
        const isLast = key === changedKeys[changedKeys.length - 1];
        const data = await settingsAPI.updateThreshold(
          key,
          editedValues[key],
          triggerHealthCheck && isLast
        );
        if (!data.success) {
          throw new Error(`Failed to update ${key}: ${data.error}`);
        }
      }

      alert(`Successfully updated ${changedKeys.length} threshold(s)!`);
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error('Error saving thresholds:', err);
      alert(`Failed to save thresholds: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const groupThresholds = (): ThresholdGroups => ({
    battery: {
      title: 'Battery Warnings',
      icon: '🔋',
      description: 'Voltage thresholds for battery warnings',
      items: thresholds.filter(
        (t) => t.setting_key.includes('battery') && !t.setting_key.endsWith('_enabled')
      ),
    },
    signal: {
      title: 'Signal Warnings',
      icon: '📡',
      description:
        'Data Rate thresholds — lower DR = worse signal. DR0 (SF12) is weakest, DR5 (SF7) excellent.',
      items: thresholds.filter(
        (t) =>
          t.setting_key.includes('signal') &&
          !t.setting_key.includes('no_signal') &&
          !t.setting_key.endsWith('_enabled')
      ),
    },
    noSignal: {
      title: 'No Signal Warnings',
      icon: '⚡',
      description:
        'Hours without a LoRa uplink before a warning is raised. One threshold per device category.',
      items: thresholds.filter(
        (t) => t.setting_key.includes('no_signal') && !t.setting_key.endsWith('_enabled')
      ),
    },
    temperature: {
      title: 'Temperature Warnings',
      icon: '🌡️',
      description:
        'Celsius thresholds — warnings trigger above the high limit or below the low limit.',
      items: thresholds.filter(
        (t) =>
          t.setting_key.startsWith('temp_') &&
          t.setting_key.includes('critical') &&
          !t.setting_key.endsWith('_enabled')
      ),
    },
    humidity: {
      title: 'Humidity Warnings',
      icon: '💧',
      description:
        'Percentage thresholds — warnings trigger above the high limit or below the low limit.',
      items: thresholds.filter(
        (t) =>
          t.setting_key.startsWith('humidity_') &&
          t.setting_key.includes('critical') &&
          !t.setting_key.endsWith('_enabled')
      ),
    },
  });

  const getSeverityLabel = (key: string): string => {
    if (key.includes('critical')) return 'Critical';
    if (key.includes('high')) return 'High';
    if (key.includes('medium')) return 'Medium';
    return '';
  };

  const getSeverityColor = (key: string): string => {
    if (key.includes('critical')) return '#f44';
    if (key.includes('high')) return '#ff9800';
    if (key.includes('medium')) return '#ffc107';
    return '#2196F3';
  };

  if (loading) {
    return (
      <div
        style={
          inline
            ? { padding: '3vmin', fontSize: '2vmin', color: '#666' }
            : {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.6)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 5000,
              }
        }
      >
        {inline ? (
          'Loading thresholds...'
        ) : (
          <div
            style={{
              background: 'white',
              padding: '3vmin',
              borderRadius: '1vmin',
              fontSize: '2vmin',
            }}
          >
            Loading thresholds...
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={
          inline
            ? { padding: '2vmin' }
            : {
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.6)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 5000,
              }
        }
        onClick={inline ? undefined : onClose}
      >
        <div
          style={{
            background: 'white',
            padding: '3vmin',
            borderRadius: '1vmin',
            maxWidth: '500px',
          }}
          onClick={inline ? undefined : (e) => e.stopPropagation()}
        >
          <h2 style={{ color: '#f44', marginTop: 0 }}>Error</h2>
          <p>{error}</p>
          <Button
            text="Close"
            onClick={onClose}
            style={{
              background: '#ccc',
              color: '#333',
              padding: '1vmin 2vmin',
              fontSize: '1.6vmin',
            }}
          />
        </div>
      </div>
    );
  }

  const groups = groupThresholds();

  return (
    <div
      style={
        inline
          ? {}
          : {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              paddingTop: 'max(20px, 5vh)',
              zIndex: 5000,
            }
      }
      onClick={inline ? undefined : onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '1vmin',
          ...(inline ? {} : { maxWidth: '800px', width: '90%', maxHeight: 'min(90vh, 85dvh)' }),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: inline ? 'none' : '0 8px 16px rgba(0,0,0,0.3)',
        }}
        onClick={inline ? undefined : (e) => e.stopPropagation()}
      >
        {/* Scrollable content area */}
        <div
          style={{
            ...scrollableModalBodyStyle(inline),
            padding: '2.5vmin',
            overscrollBehavior: 'contain',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2vmin',
              borderBottom: '2px solid #ddd',
              paddingBottom: '1.5vmin',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '2.5vmin', color: '#333' }}>
              ⚙️ Device Threshold Settings
            </h2>
            {!inline && (
              <Button
                text="✕"
                onClick={onClose}
                style={{
                  background: '#f44',
                  color: 'white',
                  fontSize: '2vmin',
                  padding: '0.5vmin 1vmin',
                  border: 'none',
                  borderRadius: '0.3vmin',
                  cursor: 'pointer',
                }}
              />
            )}
          </div>

          {/* Info message */}
          <div
            style={{
              background: '#e3f2fd',
              padding: '1.5vmin',
              borderRadius: '0.5vmin',
              marginBottom: '2vmin',
              fontSize: '1.5vmin',
              border: '1px solid #2196F3',
              color: '#1565C0',
            }}
          >
            ℹ️ Changes to these thresholds will affect how maintenance warnings are detected for all
            devices in the system.
          </div>

          {/* Threshold groups */}
          {(Object.entries(groups) as [string, ThresholdGroup][]).map(([groupKey, group]) => {
            if (group.items.length === 0) return null;

            const enabledKey = GROUP_ENABLED_KEY[groupKey];
            const isEnabled = enabledKey ? editedValues[enabledKey] !== 0 : true;

            return (
              <div
                key={groupKey}
                style={{
                  marginBottom: '2.5vmin',
                  background: '#f9f9f9',
                  padding: '1.5vmin',
                  borderRadius: '0.5vmin',
                  border: `1px solid ${isEnabled ? '#ddd' : '#f44'}`,
                  opacity: isEnabled ? 1 : 0.75,
                }}
              >
                {/* Group header with enable toggle */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '0.5vmin',
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: '2vmin',
                      color: '#333',
                      flex: 1,
                    }}
                  >
                    {group.icon} {group.title}
                  </h3>
                  {enabledKey && (
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5vmin',
                        cursor: 'pointer',
                        fontSize: '1.4vmin',
                        userSelect: 'none',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) =>
                          handleValueChange(enabledKey, e.target.checked ? '1' : '0')
                        }
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ color: isEnabled ? '#4CAF50' : '#f44', fontWeight: 'bold' }}>
                        {isEnabled ? 'Active' : 'Disabled'}
                      </span>
                    </label>
                  )}
                </div>
                <p
                  style={{
                    margin: '0 0 1.5vmin 0',
                    fontSize: '1.4vmin',
                    color: '#666',
                    fontStyle: 'italic',
                  }}
                >
                  {group.description}
                </p>

                {group.items.map((threshold) => {
                  const severityLabel = getSeverityLabel(threshold.setting_key);
                  const severityColor = getSeverityColor(threshold.setting_key);

                  return (
                    <div
                      key={threshold.setting_key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '1vmin',
                        padding: '1vmin',
                        background: 'white',
                        borderRadius: '0.3vmin',
                        border: '1px solid #e0e0e0',
                        opacity: isEnabled ? 1 : 0.5,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: '1.6vmin',
                            fontWeight: 'bold',
                            marginBottom: '0.3vmin',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5vmin',
                          }}
                        >
                          {threshold.description}
                          {severityLabel && (
                            <span
                              style={{
                                fontSize: '1.2vmin',
                                background: severityColor,
                                color: 'white',
                                padding: '0.2vmin 0.5vmin',
                                borderRadius: '0.2vmin',
                                fontWeight: 'bold',
                              }}
                            >
                              {severityLabel}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: '1.3vmin',
                            color: '#888',
                          }}
                        >
                          {threshold.setting_key}
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5vmin',
                        }}
                      >
                        <input
                          type="number"
                          step={threshold.unit === 'minutes' ? '1' : '0.1'}
                          value={editedValues[threshold.setting_key] ?? threshold.setting_value}
                          disabled={!isEnabled}
                          onChange={(e) => handleValueChange(threshold.setting_key, e.target.value)}
                          style={{
                            width: '80px',
                            fontSize: '1.6vmin',
                            padding: '0.5vmin',
                            border:
                              editedValues[threshold.setting_key] !== threshold.setting_value
                                ? '2px solid #2196F3'
                                : '1px solid #ccc',
                            borderRadius: '0.3vmin',
                            textAlign: 'center',
                            background: isEnabled ? 'white' : '#f5f5f5',
                            cursor: isEnabled ? 'text' : 'not-allowed',
                          }}
                        />
                        <span
                          style={{
                            fontSize: '1.4vmin',
                            color: '#666',
                            minWidth: '40px',
                          }}
                        >
                          {threshold.unit || ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Health check option */}
          <div
            style={{
              background: '#fff3e0',
              padding: '1.5vmin',
              borderRadius: '0.5vmin',
              marginBottom: '2vmin',
              border: '1px solid #ff9800',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1vmin',
                cursor: 'pointer',
                fontSize: '1.6vmin',
              }}
            >
              <input
                type="checkbox"
                checked={triggerHealthCheck}
                onChange={(e) => setTriggerHealthCheck(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>
                <strong>Trigger health check after save</strong>
                <div style={{ fontSize: '1.3vmin', color: '#666', marginTop: '0.3vmin' }}>
                  Re-evaluate all devices immediately with new thresholds (may take a few minutes)
                </div>
              </span>
            </label>
          </div>
        </div>
        {/* end scrollable content area */}

        {/* Footer — outside the scrollable div, always visible regardless of scroll position */}
        <div
          style={{
            flexShrink: 0,
            padding: '1.5vmin 2.5vmin',
            borderTop: '1px solid #ddd',
            background: 'white',
          }}
        >
          {hasChanges() && (
            <div
              style={{
                marginBottom: '1vmin',
                fontSize: '1.4vmin',
                color: '#2196F3',
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              ⚠️ You have unsaved changes
            </div>
          )}
          <div style={{ display: 'flex', gap: '1vmin' }}>
            <Button
              text={saving ? 'Saving...' : 'Save Changes'}
              onClick={handleSave}
              disabled={saving || !hasChanges()}
              style={{
                flex: 1,
                background: hasChanges() ? '#4CAF50' : '#ccc',
                color: 'white',
                fontSize: '1.8vmin',
                padding: '1.2vmin 2vmin',
                border: 'none',
                borderRadius: '0.5vmin',
                cursor: hasChanges() && !saving ? 'pointer' : 'not-allowed',
                opacity: hasChanges() && !saving ? 1 : 0.6,
              }}
            />
            {!inline && (
              <Button
                text="Cancel"
                onClick={onClose}
                disabled={saving}
                style={{
                  flex: 1,
                  background: '#ccc',
                  color: '#333',
                  fontSize: '1.8vmin',
                  padding: '1.2vmin 2vmin',
                  border: 'none',
                  borderRadius: '0.5vmin',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThresholdSettingsModal;
